import {
  useCallback,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
} from "react"
import {
  DEFAULT_BBOX,
  DEFAULT_CENTER,
  DEFAULT_MAP_DISPLAY_TYPE,
  DEFAULT_PADDING,
  DEFAULT_RESTRICTION,
  DEFAULT_ZOOM,
} from "@/constants/map"
import { GOOGLE_MAP_ID } from "@/constants/ui"
import { useVisibleEffectiveTheme } from "@/hooks/use-visible-effective-theme"
import { BoundingBox } from "@/types/util"
import {
  MapEvent,
  MapProps,
  Map as ReactGoogleMap,
  useMap,
} from "@vis.gl/react-google-maps"
import isEqual from "lodash/isEqual"

import { CurrentPositionMarker } from "./map/current-position-marker"
import {
  MapControls,
  type MapControlsConfig,
  type MapDisplayType,
} from "./map/map-controls"
import {
  MapStateProvider,
  type MapStateContextValue,
} from "./map/map-state-context"
import { PlacePopoverCoordinatorContext } from "./place-popover-provider"

/** Last resort when `idle` never fires again on a pooled remount (`reuseMaps`). */
const MAP_READY_FALLBACK_MS = 1500

export interface GoogleMapProps extends MapProps {
  /** Stable string for external `useMap(id)`; `useId()` for tree-only use. */
  id?: MapProps["id"]
  /** Chrome insets shared via MapStateContext. Prefer stable references. */
  padding?: google.maps.Padding
  onBoundsChange?: (bounds: BoundingBox) => void
  onZoomChange?: (zoom: number) => void
  onMapDisplayTypeChange?: (mapDisplayType: MapDisplayType) => void
  onUserInteraction?: () => void
  showMapControls?: boolean | MapControlsConfig
  showCurrentPositionMarker?: boolean
}

/**
 * Thin wrapper around `<ReactGoogleMap>`. Owns shared state (bounds,
 * zoom, initial-idle, padding) via MapStateContext. Compose layers
 * (ClusteredMarkers, NearbyMarkerLayer, MapHoverViewport, FlightOverlay,
 * MapAutoFit, MapLoadingIndicator, MapWeatherBadge, RoutePath, RouteToggle)
 * as children.
 */
export function GoogleMap({
  id,
  children,
  gestureHandling = "greedy",
  defaultCenter,
  defaultZoom,
  padding = DEFAULT_PADDING,
  onIdle,
  onBoundsChange,
  onZoomChange,
  onMapDisplayTypeChange,
  onUserInteraction,
  showMapControls = true,
  showCurrentPositionMarker = true,
}: Omit<GoogleMapProps, "scrollwheel">) {
  const effectiveTheme = useVisibleEffectiveTheme()
  const map = useMap(id)
  const popoverCoordinator = useContext(PlacePopoverCoordinatorContext)

  const [mapBounds, setMapBounds] = useState<BoundingBox>(DEFAULT_BBOX)
  const [mapZoom, setMapZoom] = useState<number | undefined>()
  const [initialIdleComplete, setInitialIdleComplete] = useState(false)
  const [mapDisplayType, setMapDisplayType] = useState<MapDisplayType>(
    DEFAULT_MAP_DISPLAY_TYPE
  )
  // Plain ref (not useLatestRef): bounds events can fire faster than
  // React commits, so an effect-based mirror would lag.
  const mapBoundsRef = useRef(mapBounds)

  const syncMapStateFromInstance = useCallback(() => {
    if (!map) return

    const zoom = map.getZoom()
    if (zoom != null) {
      setMapZoom(zoom)
      onZoomChange?.(zoom)
    }

    const bounds = map.getBounds()
    if (!bounds) return
    const ne = bounds.getNorthEast()
    const sw = bounds.getSouthWest()
    const bbox: BoundingBox = [sw.lng(), sw.lat(), ne.lng(), ne.lat()]
    if (!isEqual(bbox, mapBoundsRef.current)) {
      mapBoundsRef.current = bbox
      setMapBounds(bbox)
      onBoundsChange?.(bbox)
    }
  }, [map, onBoundsChange, onZoomChange])

  // A `colorScheme` change makes vis.gl swap in another map instance (fresh
  // or pooled), which keeps none of this one's camera. Carry it across so a
  // theme change doesn't drop the user back at the default world view.
  const lastCameraRef = useRef<google.maps.CameraOptions | null>(null)
  useLayoutEffect(() => {
    if (!map) return
    if (lastCameraRef.current) map.moveCamera(lastCameraRef.current)
    return () => {
      const center = map.getCenter()
      const zoom = map.getZoom()
      if (center && zoom != null) {
        lastCameraRef.current = {
          center,
          zoom,
          heading: map.getHeading() ?? 0,
          tilt: map.getTilt() ?? 0,
        }
      }
    }
  }, [map])

  // Pooled maps: React `onIdle` may never run again after remount. Nudge sizing, listen for Maps
  // `idle`, and always clear `initialIdleComplete` via timer so MapAutoFit cannot wedge.
  useLayoutEffect(() => {
    if (!map) return

    const syncId = window.setTimeout(syncMapStateFromInstance, 0)

    let cancelled = false
    const markReady = () => {
      if (!cancelled) {
        setInitialIdleComplete((prev) => prev || true)
        syncMapStateFromInstance()
      }
    }

    const idleListener = google.maps.event.addListenerOnce(
      map,
      "idle",
      markReady
    )
    const triggerResize = () => google.maps.event.trigger(map, "resize")
    triggerResize()
    const rafResizeId = window.requestAnimationFrame(triggerResize)

    const fallbackId = window.setTimeout(markReady, MAP_READY_FALLBACK_MS)

    return () => {
      cancelled = true
      window.clearTimeout(syncId)
      window.cancelAnimationFrame(rafResizeId)
      window.clearTimeout(fallbackId)
      google.maps.event.removeListener(idleListener)
    }
  }, [map, syncMapStateFromInstance])

  const handleBoundsChanged = () => {
    if (!map) return
    const bounds = map.getBounds()
    if (!bounds) return
    const ne = bounds.getNorthEast()
    const sw = bounds.getSouthWest()
    const bbox: BoundingBox = [sw.lng(), sw.lat(), ne.lng(), ne.lat()]
    if (!isEqual(bbox, mapBoundsRef.current)) {
      mapBoundsRef.current = bbox
      setMapBounds(bbox)
      onBoundsChange?.(bbox)
    }
  }

  const handleZoomChanged = () => {
    const zoom = map?.getZoom()
    if (zoom != null) {
      setMapZoom(zoom)
      onZoomChange?.(zoom)
    }
  }

  const handleMapIdle = (e: MapEvent) => {
    setInitialIdleComplete((prev) => prev || true)
    onIdle?.(e)
  }

  // Tapping the empty map should dismiss an open place popover. On touch,
  // Base UI's "sloppy" outside-press only fires on drag (or a synthesized
  // mouse event, which Google Maps suppresses via preventDefault), so a
  // stationary tap never reaches its dismissal path. The map's own `click`
  // event fires reliably for empty-area taps, so close the popover here.
  const handleMapClick = () => {
    popoverCoordinator?.closePlacePopover()
  }

  const handleMapDisplayTypeChange = (nextMapDisplayType: MapDisplayType) => {
    setMapDisplayType(nextMapDisplayType)
    onMapDisplayTypeChange?.(nextMapDisplayType)
  }

  const contextValue: MapStateContextValue = {
    mapBounds,
    mapZoom,
    initialIdleComplete,
    padding,
  }

  const mapControlsConfig: MapControlsConfig | null =
    showMapControls === false
      ? null
      : showMapControls === true
        ? {}
        : showMapControls

  return (
    <ReactGoogleMap
      id={id}
      reuseMaps
      className="no-translate absolute inset-0 overflow-hidden rounded-[inherit] [clip-path:border-box] [&_gmp-advanced-marker]:pointer-events-none!"
      defaultCenter={defaultCenter || DEFAULT_CENTER}
      defaultZoom={defaultZoom || DEFAULT_ZOOM}
      mapId={GOOGLE_MAP_ID}
      colorScheme={effectiveTheme === "dark" ? "DARK" : "LIGHT"}
      fullscreenControl={false}
      mapTypeControl={false}
      rotateControl={false}
      streetViewControl={false}
      scaleControl={true}
      zoomControl={false}
      cameraControl={false}
      clickableIcons={false}
      mapTypeId={mapDisplayType}
      minZoom={DEFAULT_ZOOM}
      restriction={DEFAULT_RESTRICTION}
      gestureHandling={gestureHandling}
      onBoundsChanged={handleBoundsChanged}
      onZoomChanged={handleZoomChanged}
      onIdle={handleMapIdle}
      onClick={handleMapClick}
      onDrag={onUserInteraction}
    >
      <MapStateProvider value={contextValue}>
        {mapControlsConfig && (
          <MapControls
            padding={padding}
            mapDisplayType={mapDisplayType}
            onMapDisplayTypeChange={handleMapDisplayTypeChange}
            onUserInteraction={onUserInteraction}
            config={mapControlsConfig}
          />
        )}
        {showCurrentPositionMarker && <CurrentPositionMarker />}
        {children}
      </MapStateProvider>
    </ReactGoogleMap>
  )
}
